import { queryOptions } from '@tanstack/react-query'
import { HTTPError, type Options } from 'ky'
import qs from 'query-string'
import { output, treeifyError, type ZodType } from 'zod'

import { invalidateQueries } from '@/libs/helper'

import { apiClient } from './api-client'
import { ApiError } from './api-error'

interface SchemaConfig {
  params?: ZodType
  body?: ZodType
  result?: ZodType
}

type NoExtraKeys<T, Allowed> = Exclude<keyof T, keyof Allowed> extends never ? T : never

export interface ApiConfig<TSchema extends SchemaConfig> {
  method: Options['method']
  url: string
  schema?: NoExtraKeys<TSchema, SchemaConfig>
  dependencies?: string[]
}

export type ApiInput<TSchema extends SchemaConfig | undefined> = (TSchema extends {
  params: ZodType<infer P>
}
  ? P extends object
    ? { params?: Partial<P> }
    : { params: P }
  : { params?: never }) &
  (TSchema extends { body: ZodType<infer B> } ? { body: B } : { body?: never })

export type ApiOutput<TSchema extends SchemaConfig | undefined> = TSchema extends {
  result: ZodType
}
  ? output<TSchema['result']>
  : unknown

export function apiFactory<
  TSchema extends SchemaConfig,
  TInput = ApiInput<TSchema>,
  TOutput = ApiOutput<TSchema>,
>({ method, url, schema, dependencies }: ApiConfig<TSchema>) {
  const fetchFn = async (arg?: TInput): Promise<TOutput> => {
    try {
      let searchParams: string | undefined
      let json: unknown | undefined

      if (typeof arg === 'object' && arg !== null) {
        if ('params' in arg) searchParams = qs.stringify(arg.params as Record<string, string>)
        if ('body' in arg) json = arg.body
      }

      const res = await apiClient(url, {
        method,
        searchParams,
        json,
      })

      let result = (await res.json<TOutput>()) as TOutput
      if (schema?.result) {
        const parsed = schema.result.safeParse(result)
        if (!parsed.success) {
          const msg = `[ZodError] ${url}
Response:
${JSON.stringify(result, null, 2)}
========
${JSON.stringify(treeifyError(parsed.error), null, 2)}
`
          throw Error(msg)
        }

        result = parsed.data as TOutput
      }

      if (dependencies?.length) {
        await invalidateQueries(dependencies)
      }

      return result
    } catch (error) {
      /**
       * Handle HTTP errors
       * if the response is JSON, parse it and throw an ApiError
       */
      if (
        error instanceof HTTPError &&
        error.response.headers.get('content-type')?.includes('application/json')
      ) {
        const data = await error.response.json<TErr<any>>()
        throw new ApiError(error, data)
      }

      throw error
    }
  }

  return {
    url,
    fetch: fetchFn,
    query(input?: TInput) {
      return queryOptions({
        queryKey: [url, input],
        queryFn: () => fetchFn(input),
      })
    },
    _infer: {
      input: null as TInput,
      output: null as TOutput,
    },
  }
}

export type InferApiInput<TApi extends { _infer: { input: any } }> = TApi['_infer']['input']
export type InferApiOutput<TApi extends { _infer: { output: any } }> = TApi['_infer']['output']
